import { observable, runInAction } from "mobx"
import { useEffect, useState } from "react"

import { useMyProfile } from "@/hooks/useProfile.ts"
import { useQueries } from "@/hooks/useRootContext.ts"
import { optimisticAction } from "@/lib/std.ts"

import type { useInvalidateQueries } from "@/hooks/useRootContext.ts"

export const DEV_MODE_PREFERENCE_KEY = "dev-mode"

export const devModeState = observable.box(false)

export function useDevMode() {
  const myProfile = useMyProfile()
  const queries = useQueries()

  const isDevModeAllowed = import.meta.env.DEV || (myProfile?.emails?.some(it => it.includes("jetbrains.com") || it === "matter-auto@proton.me") ?? false)

  const [isDevModeEnabled, setIsDevModeEnabled] = useState(devModeState.get())

  useEffect(() => {
    if (!isDevModeAllowed) {
      return
    }

    const fetchDevMode = async () => {
      try {
        const value = await queries.getProfilePreference(DEV_MODE_PREFERENCE_KEY)
        runInAction(() => devModeState.set(value === "true"))
        setIsDevModeEnabled(value === "true")
      } catch {
        // Ignore errors
      }
    }

    void fetchDevMode()

    const handleDevModeChanged = () => {
      setIsDevModeEnabled(devModeState.get())
    }

    window.addEventListener(DEV_MODE_PREFERENCE_KEY, handleDevModeChanged)

    return () => {
      window.removeEventListener(DEV_MODE_PREFERENCE_KEY, handleDevModeChanged)
    }
  }, [isDevModeAllowed, queries])

  return isDevModeAllowed && isDevModeEnabled
}

export async function setDevMode(queries: ReturnType<typeof useQueries>, invalidateQueries: ReturnType<typeof useInvalidateQueries>, value: boolean) {
  const oldValue = devModeState.get()
  await optimisticAction(
    () => {
      devModeState.set(value)
      window.dispatchEvent(new CustomEvent(DEV_MODE_PREFERENCE_KEY))
    },
    () => {
      return Promise.all([
        queries.apiClient.setProfilePreference(DEV_MODE_PREFERENCE_KEY, value.toString()),
        invalidateQueries({ queryKey: ["getProfilePreference", DEV_MODE_PREFERENCE_KEY] })
      ])
    },
    () => {
      devModeState.set(oldValue)
      window.dispatchEvent(new CustomEvent(DEV_MODE_PREFERENCE_KEY))
    }
  )
}
